// =====================================================================
// OvertimeBalanceBanner.jsx - Banner persistent balance overtime
// =====================================================================
// Faza 3.5 Round 1
//
// Display:
//   - Pill galben (amber) pentru sold pozitiv
//   - Pill rosu pentru sold negativ
//   - Pill gri pentru sold zero
//   - Pill galben EMPHASIZED dacă isApproachingCap (>= 35h/luna)
//
// Format: "+2.0h" / "-0.7h" / "0.0h" (per UI Odoo screenshot 2)
//
// Folosire:
//   <OvertimeBalanceBanner />          // default - vede pentru sine
//
// Permission: overtime.read.own (any logged-in user)
// Backend: GET /api/overtime/me/balance
// =====================================================================

import { useState, useEffect } from 'react';
import { overtime as overtimeApi } from '../services/api.js';

// Format decimal hours as signed hh:mm. Ex: 1.5 -> "+1:30", -0.75 -> "-0:45", 0 -> "0:00"
function formatBalanceHours(h) {
  if (h === null || h === undefined || !Number.isFinite(h) || h === 0) return '0:00';
  const sign = h > 0 ? '+' : '-';
  const totalMin = Math.round(Math.abs(h) * 60);
  const H = Math.floor(totalMin / 60);
  const M = totalMin % 60;
  return `${sign}${H}:${String(M).padStart(2, '0')}`;
}

export default function OvertimeBalanceBanner({ compact = false }) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    overtimeApi.getBalance()
      .then(data => {
        if (!cancelled) {
          setBalance(data);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          // 403 (no permission) e silent — pur si simplu nu afisam banner
          if (err.response?.status === 403) {
            setBalance(null);
            setError(null);
          } else {
            setError(err.response?.data?.message || err.message || 'Erreur chargement');
          }
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Hide while loading first time (don't show stub)
  if (loading) return null;

  // No permission or no data: hide
  if (!balance) return null;

  // Error showing as small text
  if (error) {
    return (
      <span style={{
        fontSize: 11,
        color: '#991b1b',
        padding: '2px 6px',
        background: '#fee2e2',
        borderRadius: 4,
      }}>
        Erreur sup.: {error}
      </span>
    );
  }

  const { balanceHours, currentMonthSum, isApproachingCap, isOverCap, capHoursMonth } = balance;

  // Color scheme
  let bg, fg, borderColor;
  if (isOverCap) {
    bg = '#FEE2E2'; fg = '#7F1D1D'; borderColor = '#DC2626'; // red - over cap
  } else if (isApproachingCap) {
    bg = '#FEF3C7'; fg = '#78350F'; borderColor = '#F59E0B'; // amber emphasized
  } else if (balanceHours < 0) {
    bg = '#FCEBEB'; fg = '#791F1F'; borderColor = '#E5C2C2'; // red soft - debit
  } else if (balanceHours > 0) {
    bg = '#FAEEDA'; fg = '#633806'; borderColor = '#E8D4B0'; // amber soft - credit
  } else {
    bg = '#F1F5F9'; fg = '#64748B'; borderColor = '#CBD5E1'; // gray - zero
  }

  const containerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: compact ? '4px 8px' : '6px 12px',
    background: bg,
    color: fg,
    border: `1px solid ${borderColor}`,
    borderRadius: 6,
    fontSize: compact ? 12 : 13,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  };

  // Tooltip cu detalii
  const tooltipParts = [
    `Solde cumulé: ${formatBalanceHours(balanceHours)}`,
    `Mois courant: ${formatBalanceHours(currentMonthSum)}`,
    `Plafond mensuel: ${capHoursMonth}h`,
  ];
  if (isApproachingCap && !isOverCap) {
    tooltipParts.push(`⚠ Approche du plafond`);
  }
  if (isOverCap) {
    tooltipParts.push(`⚠ Dépassement du plafond!`);
  }
  const tooltip = tooltipParts.join('\n');

  return (
    <span style={containerStyle} title={tooltip}>
      <span style={{ fontSize: compact ? 11 : 12, opacity: 0.8 }}>
        Heures sup.:
      </span>
      <strong>{formatBalanceHours(balanceHours)}</strong>
      {isApproachingCap && !isOverCap && (
        <span style={{ fontSize: 11, marginLeft: 2 }} title="Approche du plafond mensuel">⚠</span>
      )}
      {isOverCap && (
        <span style={{ fontSize: 11, marginLeft: 2 }} title="Dépassement du plafond!">⚠⚠</span>
      )}
    </span>
  );
}
